import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Eye, EyeOff, Shield, AlertCircle, Loader2, CheckCircle2, Lock } from 'lucide-react';

const inputStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
};

const SetupPasswordPage = () => {
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionError, setSessionError] = useState('');

  // On mount: parse the hash fragment from the invite URL and establish session
  useEffect(() => {
    const handleInviteToken = async () => {
      const hash = window.location?.hash;

      // Parse hash params: #access_token=...&refresh_token=...&type=invite
      const params = new URLSearchParams(hash.replace('#', ''));
      const accessToken = params?.get('access_token');
      const refreshToken = params?.get('refresh_token');
      const type = params?.get('type');

      if (accessToken && (type === 'invite' || type === 'recovery' || type === 'signup')) {
        try {
          // Set the session using the tokens from the invite link
          const { data, error: sessionError } = await supabase?.auth?.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
          });

          if (sessionError) {
            setSessionError('Your invite link has expired or is invalid. Please request a new invite.');
            return;
          }

          if (data?.session) {
            // Clear the hash from the URL for cleanliness
            window.history?.replaceState(null, '', window.location?.pathname);
            setSessionReady(true);
          }
        } catch (err) {
          setSessionError('Failed to process invite link. Please try again or request a new invite.');
        }
      } else {
        // No token in URL — check if already has a valid session (e.g. password reset flow)
        const { data: { session } } = await supabase?.auth?.getSession();
        if (session) {
          setSessionReady(true);
        } else {
          setSessionError('No valid invite token found. Please use the link from your invitation email.');
        }
      }
    };

    handleInviteToken();
  }, []);

  const validatePassword = () => {
    if (password?.length < 8) return 'Password must be at least 8 characters.';
    if (password !== confirmPassword) return 'Passwords do not match.';
    return null;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const validationError = validatePassword();
    if (validationError) { setError(validationError); return; }

    setLoading(true);
    setError('');

    try {
      const { error: updateError } = await supabase?.auth?.updateUser({ password });

      if (updateError) {
        setError(updateError?.message || 'Failed to set password. Please try again.');
        return;
      }

      setSuccess(true);

      // Fetch user profile to determine role-based redirect
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) {
        const { data: profile } = await supabase?.from('user_profiles')?.select('role')?.eq('id', user?.id)?.single();

        const role = profile?.role;

        setTimeout(() => {
          if (role === 'admin') {
            navigate('/admin-dashboard', { replace: true });
          } else if (role === 'driver') {
            navigate('/driver-dashboard-screen', { replace: true });
          } else if (role === 'host') {
            navigate('/host-dashboard', { replace: true });
          } else {
            navigate('/car-hire-listing', { replace: true });
          }
        }, 2000);
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = () => {
    if (!password) return null;
    if (password?.length < 6) return { label: 'Too short', color: '#ef4444', width: '25%' };
    if (password?.length < 8) return { label: 'Weak', color: '#f97316', width: '40%' };
    if (password?.length < 12 && !/[^a-zA-Z0-9]/?.test(password)) return { label: 'Fair', color: '#eab308', width: '60%' };
    if (password?.length >= 8 && /[A-Z]/?.test(password) && /[0-9]/?.test(password)) return { label: 'Strong', color: '#22c55e', width: '100%' };
    return { label: 'Good', color: '#ffd700', width: '80%' };
  };

  const strength = passwordStrength();

  return (
    <div
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #060e1e 0%, #0a1628 50%, #0d1f3c 100%)' }}
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-5" style={{ background: 'radial-gradient(circle, #ffd700, transparent)' }} />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full opacity-5" style={{ background: 'radial-gradient(circle, #ffd700, transparent)' }} />
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'linear-gradient(rgba(255,215,0,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,215,0,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
      </div>
      <div className="relative w-full max-w-md mx-auto px-4">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4" style={{ background: 'linear-gradient(135deg, #ffd700, #e6c200)' }}>
            <Lock className="w-8 h-8" style={{ color: '#0a1628' }} />
          </div>
          <h1 className="text-3xl font-bold text-white tracking-tight">
            On<span style={{ color: '#ffd700' }}>TheGo</span>
          </h1>
          <p className="text-white/40 text-sm mt-1 tracking-widest uppercase">Account Setup</p>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-white/10 p-8" style={{ background: 'rgba(13, 31, 60, 0.8)', backdropFilter: 'blur(20px)' }}>

          {/* Session error state */}
          {sessionError ? (
            (<div className="text-center py-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(239,68,68,0.15)' }}>
                <AlertCircle className="w-6 h-6 text-red-400" />
              </div>
              <h2 className="text-lg font-semibold text-white mb-2">Invalid Invite Link</h2>
              <p className="text-white/50 text-sm leading-relaxed">{sessionError}</p>
              <button
                onClick={() => navigate('/', { replace: true })}
                className="mt-6 text-sm transition-colors"
                style={{ color: '#ffd700' }}
              >
                ← Return to Home
              </button>
            </div>)

          /* Success state */
          ) : success ? (
            (<div className="text-center py-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(34,197,94,0.15)' }}>
                <CheckCircle2 className="w-7 h-7 text-green-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Password Set Successfully!</h2>
              <p className="text-white/50 text-sm">Your account is ready. Redirecting you to your dashboard…</p>
              <div className="mt-4 flex justify-center">
                <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#ffd700' }} />
              </div>
            </div>)

          /* Loading session state */
          ) : !sessionReady ? (
            (<div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: '#ffd700' }} />
              <p className="text-white/50 text-sm">Verifying your invite link…</p>
            </div>)

          /* Password setup form */
          ) : (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-white">Set Your Password</h2>
                <p className="text-white/40 text-sm mt-1">Create a secure password to complete your account setup</p>
              </div>

              {error && (
                <div className="flex items-start gap-3 p-3 rounded-xl mb-5 border" style={{ background: 'rgba(239,68,68,0.1)', borderColor: 'rgba(239,68,68,0.3)' }}>
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* New Password */}
                <div>
                  <label className="block text-white/60 text-sm font-medium mb-2">New Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => { setPassword(e?.target?.value); setError(''); }}
                      placeholder="Minimum 8 characters"
                      autoComplete="new-password"
                      className="w-full px-4 py-3 pr-12 rounded-xl text-white placeholder-white/20 text-sm outline-none transition-all"
                      style={inputStyle}
                      onFocus={(e) => { e.target.style.borderColor = '#ffd700'; e.target.style.boxShadow = '0 0 0 2px rgba(255,215,0,0.15)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {/* Password strength bar */}
                  {strength && (
                    <div className="mt-2">
                      <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-300"
                          style={{ width: strength?.width, background: strength?.color }}
                        />
                      </div>
                      <p className="text-xs mt-1" style={{ color: strength?.color }}>{strength?.label}</p>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-white/60 text-sm font-medium mb-2">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e?.target?.value); setError(''); }}
                      placeholder="Re-enter your password"
                      autoComplete="new-password"
                      className="w-full px-4 py-3 pr-12 rounded-xl text-white placeholder-white/20 text-sm outline-none transition-all"
                      style={inputStyle}
                      onFocus={(e) => { e.target.style.borderColor = '#ffd700'; e.target.style.boxShadow = '0 0 0 2px rgba(255,215,0,0.15)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                    >
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {/* Match indicator */}
                  {confirmPassword && (
                    <p className="text-xs mt-1" style={{ color: password === confirmPassword ? '#22c55e' : '#ef4444' }}>
                      {password === confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !password || !confirmPassword}
                  className="w-full py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ background: 'linear-gradient(135deg, #ffd700, #e6c200)', color: '#0a1628' }}
                  onMouseEnter={(e) => { if (!loading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Setting Password…</> : 'Set Password & Continue'}
                </button>
              </form>
            </>
          )}
        </div>

        {/* Security notice */}
        <div className="flex items-center justify-center gap-2 mt-6">
          <Shield className="w-3.5 h-3.5 text-white/20" />
          <p className="text-white/20 text-xs">Your connection is secure · All data is encrypted</p>
        </div>
      </div>
    </div>
  );
};

export default SetupPasswordPage;
